import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProduct } from '../../contexts/ProductContext';

const ProductCard = ({ product, searchTerm }) => {
  const { t } = useTranslation();
  const { openProduct } = useProduct();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    // Check if product is in favorites on mount and when product changes
    const favorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
    // Convert to strings for comparison to handle both string and number IDs
    setIsFavorite(favorites.map(id => String(id)).includes(String(product.id)));
  }, [product.id]);

  const handleFavoriteClick = (e) => {
    e.preventDefault(); // Prevent navigation when clicking the favorite button
    e.stopPropagation();
    
    const favorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
    
    if (isFavorite) {
      // Use String comparison to handle both string and number IDs
      const newFavorites = favorites.filter(id => String(id) !== String(product.id));
      localStorage.setItem('favoriteProducts', JSON.stringify(newFavorites));
      setIsFavorite(false);
    } else {
      favorites.push(product.id);
      localStorage.setItem('favoriteProducts', JSON.stringify(favorites));
      setIsFavorite(true);
    }
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  const highlightMatch = (text, search) => {
    if (!search || !text) return text;
    
    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === search.toLowerCase() ? 
        <mark key={index} className="bg-yellow-200 px-0.5">{part}</mark> : 
        part
    );
  };

  const handleProductClick = (e) => {
    e.preventDefault();
    openProduct(product.id);
  };

  return (
    <div className="group relative bg-white rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <div
        onClick={handleProductClick}
        className="block cursor-pointer"
      >
        <div className="relative aspect-square bg-white">
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse" />
          )}
          <img
            src={product.image}
            alt={product.name}
            className={`w-full h-full object-contain transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            } group-hover:scale-105 transition-transform duration-300`}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
          />
          {product.isNew && (
            <span className="absolute bottom-2 left-2 bg-bmb-text text-white text-xs px-2 py-1 rounded">
              Nova Coleção
            </span>
          )}
          {product.collection && (
            <span className="absolute top-2 right-2 bg-primary-100 text-bmb-text text-xs px-2 py-1 rounded">
              {product.collection}
            </span>
          )}
        </div>
        
        <div className="p-4">
          <div className="text-xs text-gray-500 mb-1 font-mono">
            SKU: {highlightMatch(product.sku, searchTerm)}
          </div>
          <h3 className="text-bmb-text font-light text-sm mb-1 group-hover:text-primary-700 transition-colors">
            {highlightMatch(product.name, searchTerm)}
          </h3>
          <p className="text-gray-600 text-xs mb-2">{product.material}</p>
          
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-500">{product.category}</span>
          </div>
        </div>
      </div>
      
      {/* Favorite Button */}
      <button
        onClick={handleFavoriteClick}
        className={`absolute top-3 left-3 p-2 rounded-full transition-all duration-200 ${
          isFavorite 
            ? 'bg-red-500 text-white shadow-md hover:bg-red-600' 
            : 'bg-white/90 text-gray-600 shadow-md hover:bg-white hover:text-red-500'
        } z-10`}
        title={isFavorite ? t('product.remove_from_favorites') : t('product.add_to_favorites')}
        aria-label={isFavorite ? t('product.remove_from_favorites') : t('product.add_to_favorites')}
      >
        <svg 
          className="w-5 h-5" 
          fill={isFavorite ? "currentColor" : "none"}
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
          />
        </svg>
      </button>
    </div>
  );
};

export default ProductCard;